export const WORK_TYPES = [
  { key: "routine", label: "Routine Maintenance" },
  { key: "breakdown", label: "Breakdown Repair" },
  { key: "safety_audit", label: "Safety Audit" },
  { key: "servicing", label: "General Servicing" },
];
export const workTypeLabel = (k) => WORK_TYPES.find((w) => w.key === k)?.label || k;

// ---- Mobile Catering Units & Food Trailers (SFBB / HACCP) ----
export const FOOD_REGIONS = [
  { key: "uk", label: "England / Wales / NI", reheat: 75 },
  { key: "scotland", label: "Scotland", reheat: 82 },
];
export const foodRegionLabel = (k) => FOOD_REGIONS.find((r) => r.key === k)?.label || "England / Wales / NI";
export const reheatTarget = (k) => FOOD_REGIONS.find((r) => r.key === k)?.reheat || 75;

export const CATERING_SESSIONS = [
  { key: "opening", label: "Opening Checks", icon: "sunny", color: "#f59e0b" },
  { key: "service", label: "Service Checks", icon: "restaurant", color: "#ef4444" },
  { key: "midday", label: "Mid-Day Temp Check", icon: "thermometer", color: "#0ea5e9" },
  { key: "closing", label: "Closing Checks", icon: "moon", color: "#3b82f6" },
  { key: "weekly", label: "Weekly Checks", icon: "calendar", color: "#8b5cf6" },
];
export const cateringSessionLabel = (k) => (k === "delivery" ? "Delivery Log" : CATERING_SESSIONS.find((s) => s.key === k)?.label || k);

export const DELIVERY_TYPES = [
  { key: "chilled", label: "Chilled", max: 8 },
  { key: "frozen", label: "Frozen", max: -15 },
  { key: "ambient", label: "Ambient", max: null },
];
export const deliveryTypeLabel = (k) => DELIVERY_TYPES.find((t) => t.key === k)?.label || k;
export const DELIVERY_CORRECTIVES = ["Returned to Driver", "Rejected & Refund Requested", "Moved to Backup Chiller", "Disposed Of"];

export const CORRECTIVE_ACTIONS = ["Adjusted thermostat", "Stock moved to another unit", "Delivery rejected & returned to vendor", "Food discarded", "Equipment repaired / serviced", "Other"];

// Dynamic multi-appliance cold storage + multi-item cooking logs.
export const APPLIANCE_SUGGESTIONS = ["Main Unit Fridge", "Under-Counter Chiller", "External Trailer Fridge", "Display Freezer", "Counter Chiller", "Chest Freezer"];
export const APPLIANCE_TYPES = [
  { key: "chilled", label: "Chilled", max: 8, target: "≤ 8°C" },
  { key: "frozen", label: "Frozen", max: -15, target: "≤ -18°C" }, // storage target -18°C; corrective action if warmer than -15°C
];
export const applianceTypeDef = (k) => APPLIANCE_TYPES.find((t) => t.key === k) || APPLIANCE_TYPES[0];
export const TEMP_CORRECTIVES = ["Adjusted Thermostat", "Stock Moved to Chiller", "Re-cooked to Target Temp", "Discarded Food"];
export const COOK_MIN = 75;

// Preset SFBB/HACCP food-safety checklists. Temperature items carry a safe
// threshold (min OR max). A reading that breaches forces a corrective action.
// kind: "temp" | "pass_fail" | "text" | "appliance_group" | "cooking_group"
export function cateringChecklist(session, region) {
  const reheat = reheatTarget(region);
  const T = (id, label, opts) => ({ id, label, kind: "temp", unit: "°C", ...opts });
  const PF = (id, label, help) => ({ id, label, kind: "pass_fail", help });
  const TX = (id, label, help) => ({ id, label, kind: "text", help });
  const APPL = (id, label, help) => ({ id, label, kind: "appliance_group", help });
  const COOK = (id, label, help) => ({ id, label, kind: "cooking_group", help });
  if (session === "opening") return [
    APPL("cold_storage", "Cold storage temperatures", "Log every fridge, chiller & freezer. Chilled must be ≤ 8°C · Frozen target ≤ -18°C (corrective action if warmer than -15°C)."),
    PF("lpg", "LPG gas check", "No smell of gas, hoses in date & undamaged, regulator secure, soapy-water bubble test done."),
    PF("handwash", "Handwash station stocked", "Hot water, soap and paper towels available and working."),
    PF("clean_water", "Clean water tank filled", "Sufficient potable water for service."),
    PF("waste_water", "Waste water tank empty", "Grey-water tank emptied with capacity available."),
  ];
  if (session === "service") return [
    COOK("cooking", "Core cooking temperatures", `Log each menu item cooked. Core must reach ${COOK_MIN}°C (or 70°C held for 2 minutes).`),
    T("hot_hold", "Hot holding temperature", { min: 63, help: "Hold hot food at 63°C or above." }),
    T("reheat", "Reheating temperature", { min: reheat, help: `Reheat to ${reheat}°C (${region === "scotland" ? "Scotland" : "England / Wales / NI"} standard).` }),
    PF("probe", "Probe thermometer sanitised", "Cleaned & sanitised between uses; anti-bacterial probe wipes used."),
  ];
  if (session === "midday") return [
    APPL("cold_storage", "Mid-service cold storage temperatures", "Quick mid-day check of every fridge, chiller & freezer during service. Chilled ≤ 8°C · Frozen target ≤ -18°C (action if warmer than -15°C)."),
  ];
  if (session === "closing") return [
    APPL("cold_storage", "Final cold storage temperatures", "End-of-day reading for every fridge, chiller & freezer. Chilled ≤ 8°C · Frozen target ≤ -18°C (action if warmer than -15°C)."),
    PF("cooling", "90-minute cooling compliance", "Any hot food cooled from 63°C to 8°C or below within 90 minutes, then chilled/frozen."),
    PF("pest", "Pest check", "No signs of pests — droppings, gnaw marks, insects or nesting."),
    PF("waste", "Waste disposed correctly", "Food & general waste removed, bins cleaned, no build-up."),
  ];
  return [
    PF("deep_clean", "Deep cleaning sign-off", "Scheduled deep clean of surfaces, equipment and extraction completed."),
    PF("allergen", "Natasha's Law allergen review", "Allergen matrix reviewed & PPDS labels accurate (ingredients + 14 allergens emphasised)."),
    TX("weekly_notes", "Weekly review notes", "Any issues, supplier problems or actions for the week ahead."),
  ];
}

export const catIcon = (category) => (category === "catering" ? "restaurant" : category === "inflatable" ? "balloon" : "cog");
export const catLabel = (category) => (category === "catering" ? "Mobile Catering Unit" : category === "inflatable" ? "Inflatable / Bouncy Castle" : "Ride / Mechanical");

// The 14 FSA-declarable allergens (Natasha's Law) — used by the Allergen Matrix Manager.
export const FSA_ALLERGENS = [
  { key: "celery", label: "Celery", code: "CE" },
  { key: "gluten", label: "Cereals w/ Gluten", code: "GL" },
  { key: "crustaceans", label: "Crustaceans", code: "CR" },
  { key: "eggs", label: "Eggs", code: "EG" },
  { key: "fish", label: "Fish", code: "FI" },
  { key: "lupin", label: "Lupin", code: "LU" },
  { key: "milk", label: "Milk", code: "MK" },
  { key: "molluscs", label: "Molluscs", code: "MO" },
  { key: "mustard", label: "Mustard", code: "MU" },
  { key: "tree_nuts", label: "Tree Nuts", code: "TN" },
  { key: "peanuts", label: "Peanuts", code: "PE" },
  { key: "sesame", label: "Sesame", code: "SE" },
  { key: "soya", label: "Soya", code: "SY" },
  { key: "sulphites", label: "Sulphur Dioxide / Sulphites", code: "SD" },
];

// FSA SFBB cleaning schedule frequencies.
export const CLEANING_FREQUENCIES = [
  { key: "after_use", label: "After Use" },
  { key: "every_shift", label: "Every Shift" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "other", label: "Other" },
];
export const cleaningFreqLabel = (k) => CLEANING_FREQUENCIES.find((f) => f.key === k)?.label || "Other";
// Tasks that auto-appear on a shift execution checklist.
export const SHIFT_FREQUENCIES = ["every_shift", "daily"];
